import React, { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { getActionConfig } from '@/config/audit-logs.config';
import { AuditDetailsList, AuditTechnicalBlock, AuditTechnicalData } from './AuditLogDetails';
import type { AuditLog } from '@/features/external-users/types/audit-log.types';

interface CreationTimelineItemProps {
  mainLog: AuditLog;
  detailLogs: AuditLog[];
}

const getActionPriority = (action: string): number => {
  if (action === 'INIT') return 0;
  if (action.startsWith('API_CALL_')) return 1;
  if (action === 'S3_UPLOAD') return 2;
  if (action === 'VALIDATION') return 3;
  if (action === 'CODE_GENERATION') return 4;
  if (action === 'PARTS_VALIDATION') return 5;
  if (action === 'METIER_INTEGRATION') return 6;
  if (action === 'DB_SAVE') return 7;
  if (action === 'PARTS_LINKING') return 8;
  if (action === 'STEP_SUCCESS') return 9;
  if (action === 'COMPLETE') return 10;
  if (action === 'STEP_ERROR') return 98;
  if (action === 'CREATION_FAILED') return 99;
  if (action === 'ROLLBACK') return 99;
  return 50;
};

const isError = (log: AuditLog) => log.status === 'ERROR' || log.action === 'STEP_ERROR';

/** Une étape de la création, telle que l'a tracée le code : réservée au bloc technique. */
const TimelineStep: React.FC<{ log: AuditLog }> = ({ log }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const stepConfig = getActionConfig(log.action);
  const StepIcon = stepConfig.icon;
  const data = log.newData && Object.keys(log.newData).length > 0 ? log.newData : null;

  return (
    <div className="tw-relative">
      <div className="tw-absolute -tw-left-[1.3rem] tw-w-4 tw-h-4 tw-rounded-full tw-bg-white tw-border-2 tw-border-gray-300 tw-flex tw-items-center tw-justify-center">
        {isError(log) ? (
          <div className="tw-w-2 tw-h-2 tw-rounded-full tw-bg-red-500" />
        ) : log.status === 'SUCCESS' || log.action === 'COMPLETE' ? (
          <div className="tw-w-2 tw-h-2 tw-rounded-full tw-bg-green-500" />
        ) : (
          <div className="tw-w-2 tw-h-2 tw-rounded-full tw-bg-blue-100" />
        )}
      </div>

      <div className="tw-bg-gray-50 tw-rounded-lg tw-p-3">
        <div className="tw-flex tw-items-start tw-justify-between tw-gap-2">
          <div className="tw-flex tw-items-center tw-gap-2 tw-flex-1">
            <StepIcon className={`tw-w-4 tw-h-4 ${stepConfig.colorClass.split(' ')[1]}`} />
            <span className="tw-text-sm tw-font-medium tw-text-gray-900">
              {log.actionLabel || stepConfig.label}
            </span>
            {log.stepName && <span className="tw-text-xs tw-text-gray-500">{log.stepName}</span>}
          </div>
          <div className="tw-flex tw-items-center tw-gap-2 tw-text-xs tw-text-gray-500">
            {log.durationMs ? <span>{log.durationMs} ms</span> : null}
            <span>{format(new Date(log.createdAt), 'HH:mm:ss', { locale: fr })}</span>
          </div>
        </div>

        {log.changesSummary && (
          <p className="tw-mt-2 tw-mb-0 tw-text-xs tw-text-gray-600">{log.changesSummary}</p>
        )}

        {data && (
          <div className="tw-mt-2">
            <button
              type="button"
              onClick={() => setIsExpanded(!isExpanded)}
              className="tw-text-xs tw-text-blue-600 hover:tw-text-blue-700"
            >
              {isExpanded ? 'Masquer les données' : 'Voir les données'}
            </button>
            {isExpanded && (
              <div className="tw-mt-2">
                <AuditTechnicalData data={data} />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Une création (contrat, garantie, société, utilisateur) regroupée en une
 * seule entrée.
 *
 * Ce qui compte pour le lecteur vient de la ligne de fin — mode de signature,
 * de paiement, envois — et des étapes en échec, mises en forme par l'API. Les
 * quinze étapes tracées par le code ne sont montrées que dans le bloc
 * technique : elles disent comment la création s'est faite, pas ce qu'elle a
 * produit.
 */
export const CreationTimelineItem: React.FC<CreationTimelineItemProps> = ({ mainLog, detailLogs }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const config = getActionConfig('CREATE');
  const IconComponent = config.icon;

  const sortedDetailLogs = [...detailLogs].sort((a, b) => {
    const priorityDiff = getActionPriority(a.action) - getActionPriority(b.action);
    if (priorityDiff !== 0) return priorityDiff;
    return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
  });

  const details = sortedDetailLogs
    .filter((log) => log.action === 'COMPLETE' || log.action === 'STEP_ERROR')
    .flatMap((log) => log.details ?? []);

  const hasError = sortedDetailLogs.some(isError);
  const statusColor = hasError ? 'tw-text-red-600' : 'tw-text-green-600';
  const statusText = hasError ? 'Erreur' : 'Succès';

  return (
    <div className="hover:tw-bg-gray-50 tw-transition-colors">
      <div className="tw-px-4 tw-py-3">
        <div className="tw-flex tw-items-center tw-justify-between tw-gap-4">
          <div className="tw-flex tw-items-center tw-gap-3 tw-flex-1 tw-min-w-0">
            <div className="tw-flex-shrink-0">
              <div className={`tw-w-8 tw-h-8 tw-rounded-full tw-flex tw-items-center tw-justify-center ${config.colorClass}`}>
                <IconComponent className="tw-w-4 tw-h-4" />
              </div>
            </div>

            <div className="tw-flex tw-items-center tw-gap-2 tw-min-w-0 tw-flex-wrap">
              <span className="tw-text-sm tw-font-medium tw-text-gray-900 tw-truncate">
                Création
              </span>
              {(mainLog.userName || mainLog.userEmail) && (
                <span className="tw-text-xs tw-text-gray-700">
                  - Par {mainLog.userName || mainLog.userEmail}
                </span>
              )}
              <span className={`tw-text-xs tw-font-medium ${statusColor}`}>
                - {statusText}
              </span>
            </div>
          </div>

          <div className="tw-flex-shrink-0 tw-text-xs tw-text-gray-500">
            {format(new Date(mainLog.createdAt), 'dd/MM/yyyy HH:mm', { locale: fr })}
          </div>
        </div>

        <div className="tw-mt-2">
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="tw-flex tw-items-center tw-gap-1.5 tw-text-xs tw-font-medium tw-text-affinicar-primary hover:tw-text-affinicar-secondary tw-transition-colors"
          >
            {isExpanded ? (
              <>
                <ChevronDown className="tw-w-3.5 tw-h-3.5" />
                <span>Masquer les détails</span>
              </>
            ) : (
              <>
                <ChevronRight className="tw-w-3.5 tw-h-3.5" />
                <span>Voir les détails</span>
              </>
            )}
          </button>

          {isExpanded && (
            <div className="tw-mt-3 tw-pt-3 tw-border-t tw-border-gray-200 tw-space-y-3">
              <AuditDetailsList details={details} />

              <AuditTechnicalBlock>
                <div className="tw-ml-4 tw-border-l-2 tw-border-gray-200 tw-pl-4 tw-space-y-3">
                  {[mainLog, ...sortedDetailLogs].map((log) => (
                    <TimelineStep key={log.id} log={log} />
                  ))}
                </div>
              </AuditTechnicalBlock>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
